import React from 'react';
import {
  Button,
  Heading,
  Pagination,
  SearchInput,
} from '@alefeducation/ui-elements-library-core';
import { PlusCircleSolid } from '@alefeducation/ui-elements-library-icons';
import styled from 'styled-components';
import {
  StyledOrgContainer,
  StyledTableBody,
  StyledAddOrgButtonContainer,
  StyledTableHeader,
} from './Organisations.styled';
import useOrganisations from './hooks/useOrganisations';
import {
  NoItemsView,
  OrganisationsRows,
  OrgColHeader,
  StatusUpdateConfirmModal,
} from '../components/Organisations';
import { OrganisationEditorModal } from '../components/OrganisationEditorModal/OrganisationEditorModal';
import Loading from '../components/Loading';
import PageStatus from '../components/Pagination/PageStatus';
import {
  ADD_ORG_BUTTON_TEXT,
  NO_ORGANISATIONS_DESCRIPTION_MESSAGE,
  NO_ORGANISATIONS_TITLE_MESSAGE,
  OOPS_SOMETHING_WENT_WRONG_MESSAGE,
  ORGANISATIONS,
  SEARCH_BOX_PLACEHOLDER,
} from '../utils/constants';

const StyledPaginationContainer = styled.div`
  margin: ${({ theme: { space } }: any) => `${space[24]}px`};
`;

const StyledContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 20px;
  margin-right: 25px;
`;

const Organisations = () => {
  const {
    orgResponse: { isLoading, error, organisationsPage },
    modal: {
      search,
      currentPage,
      totalPages,
      showEditModal,
      setEditModal,
      showStatusModal,
      setStatusModal,
      onStatusUpdateConfirmation,
      onSearchDebounce,
      setSearch,
      setCurrentPage,
    },
    menuActions: { onEditClick, onStatusClick },
    organisationInEdit,
    onAddOrgClick,
    isViewOnlyRole,
    pageStats,
  } = useOrganisations();

  const showPagination = () => {
    const items = organisationsPage.organisations || [];
    return (
      organisationsPage.totalCount !== 0 && items.length > 0 && totalPages > 0
    );
  };

  return (
    <React.Fragment>
      <StyledContainer>
        <SearchInput
          width="310px"
          value={search}
          placeholder={SEARCH_BOX_PLACEHOLDER}
          onChange={(e) => {
            onSearchDebounce(e.target.value);
          }}
          onClear={() => {
            setSearch('');
            setCurrentPage(1);
          }}
          onSearch={() => {}}
        />
      </StyledContainer>
      <StyledOrgContainer>
        <StyledTableHeader>
          <Heading variant="heading-medium">{ORGANISATIONS}</Heading>
          {!isViewOnlyRole && (
            <StyledAddOrgButtonContainer>
              <Button icon={<PlusCircleSolid />} onClick={onAddOrgClick}>
                {ADD_ORG_BUTTON_TEXT}
              </Button>
            </StyledAddOrgButtonContainer>
          )}
        </StyledTableHeader>
        <OrgColHeader />

        {isLoading && <Loading />}

        {error && <NoItemsView title={OOPS_SOMETHING_WENT_WRONG_MESSAGE} />}

        {!isLoading && !error && (
          <StyledTableBody>
            {organisationsPage.totalCount === 0 ? (
              <NoItemsView
                title={NO_ORGANISATIONS_TITLE_MESSAGE}
                description={NO_ORGANISATIONS_DESCRIPTION_MESSAGE}
              />
            ) : (
              <OrganisationsRows
                organisations={organisationsPage.organisations ?? []}
                onEditClick={onEditClick}
                onStatusClick={onStatusClick}
                isViewOnlyRole={isViewOnlyRole}
              />
            )}
          </StyledTableBody>
        )}
        {showPagination() && (
          <StyledPaginationContainer>
            <Pagination
              initialPage={currentPage}
              onPageChange={(page) => {
                setCurrentPage(page);
              }}
              pages={totalPages}
              data-testid="pagination"
            />
            <PageStatus isHidden={isLoading}>{pageStats}</PageStatus>
          </StyledPaginationContainer>
        )}
        <OrganisationEditorModal
          setShowModal={setEditModal}
          open={showEditModal}
          organisation={organisationInEdit}
          isEdit={organisationInEdit !== undefined}
          isViewOnlyRole={isViewOnlyRole}
        />
        <StatusUpdateConfirmModal
          isActive={organisationInEdit?.isActive || false}
          isOpen={showStatusModal}
          modalVisibility={setStatusModal}
          onYesClick={onStatusUpdateConfirmation}
        />
        <div id="organisations-page-toast" />
      </StyledOrgContainer>
    </React.Fragment>
  );
};

export default Organisations;
